/**
 * API Configuration for iLand Voice Bot Widget
 * 
 * Automatically detects the API base URL from:
 * 1. Explicit window.__ILAND_API_ENDPOINT__ (set via apiEndpoint config)
 * 2. The widget script's src URL
 * 3. Fallback to production URL
 */

// Production base URL
const PRODUCTION_BASE_URL = 'https://i-lands.vercel.app'

/**
 * Get the API base URL for the widget
 * This allows the widget to work when embedded on any website
 */
export function getApiBaseUrl(): string {
  // 1. Check for explicit API endpoint
  const explicitEndpoint = (window as unknown as { __ILAND_API_ENDPOINT__?: string }).__ILAND_API_ENDPOINT__
  if (explicitEndpoint) {
    return explicitEndpoint.replace(/\/$/, '') // Remove trailing slash
  }

  // 2. Try to detect from the widget script's src
  try {
    const scripts = document.querySelectorAll('script[src*="widget.js"]')
    for (const script of scripts) {
      const src = script.getAttribute('src')
      if (src && (src.includes('i-lands') || src.includes('ilands') || src.includes('vercel.app'))) {
        const url = new URL(src, window.location.href)
        return `${url.protocol}//${url.host}`
      }
    }
  } catch {
    // Ignore URL parsing errors
  }

  // 3. If we're on a vercel.app domain, use the current origin
  if (typeof window !== 'undefined' && window.location.origin.includes('vercel.app')) {
    return window.location.origin
  }

  // 4. Fallback to production URL
  return PRODUCTION_BASE_URL
}

// Cache the base URL after first calculation
let cachedBaseUrl: string | null = null

/**
 * Get the cached API base URL
 * Calculates once and caches for performance
 */
export function getApiUrl(path: string): string {
  if (!cachedBaseUrl) {
    cachedBaseUrl = getApiBaseUrl()
  }
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${cachedBaseUrl}${normalizedPath}`
}
